import type { EnvironmentManager } from '@/environment/EnvironmentManager';
import { WEATHER_PRESETS, type GroundCoverState } from '@/environment/EnvironmentState';
import { ICE_SWATCHES, SNOW_SWATCHES, iceSwatch, snowSwatch, type WinterSwatch } from '@/terrain/WinterGround';
import { clear, el, setClass } from './dom';
import { icon } from './Icons';

/**
 * Environment tray (spec §13): site latitude and weather presets. Sits under the inspector in
 * the right dock, mirroring the SketchUp tray convention of stacked collapsible panels.
 *
 * Time of day and date used to live at the top of this panel and now do not: they are the two
 * controls that change every surface in the scene, they get moved constantly, and burying them in a
 * tray made you look away from the model to use them. They are the floating {@link ChronoBar} at the
 * top centre of the frame instead. What is left here is the set-once material — where the site is,
 * how the air moves, what the sky is doing — which is exactly what a tray is for.
 *
 * Every control writes straight to {@link EnvironmentManager}, which recomputes the sun and notifies
 * the renderer — the panel holds no environment state of its own.
 */

export interface EnvironmentPanelDeps {
  environment: EnvironmentManager;
  /** Called after any change so the rest of the HUD (thumbnails, inspector) can follow the season. */
  onChange: () => void;
}

export class EnvironmentPanel {
  readonly root: HTMLElement;
  private readonly body: HTMLElement;
  /** The winter state the current DOM was built for. See {@link syncSeason}. */
  private builtWinter = '';
  /**
   * The latitude slider and its readout, so {@link syncSite} can move them in place.
   *
   * Held rather than looked up because localizing the site *sets* latitude — the site bar above the
   * palette writes it — and a tray still showing 45° after the site moved to New Jersey would be
   * quietly wrong about the one number the sun is computed from.
   */
  private latitude: { input: HTMLInputElement; readout: HTMLElement } | null = null;

  constructor(private readonly deps: EnvironmentPanelDeps) {
    this.body = el('div', { class: 'lg-panel__body' });
    this.root = el(
      'aside',
      { class: 'lg-panel lg-panel--right lg-panel--secondary', attrs: { 'aria-label': 'Environment' } },
      el(
        'div',
        { class: 'lg-panel__head' },
        el('span', { class: 'lg-panel__icon', html: icon('sun', 16) }),
        el('h2', { class: 'lg-panel__title', text: 'Environment' }),
        el('button', {
          class: 'lg-panel__collapse',
          html: icon('chevron', 16),
          title: 'Collapse environment',
          attrs: { type: 'button', 'aria-label': 'Collapse environment' },
          on: { click: () => setClass(this.root, 'is-collapsed', !this.root.classList.contains('is-collapsed')) },
        }),
      ),
      this.body,
    );
    this.refresh();
  }

  refresh(): void {
    const state = this.deps.environment.state;
    clear(this.body);

    /* -------------------------------- Winter ground -------------------------------- */
    // First in the tray while it applies, and last when it does not.
    //
    // Not a cosmetic ordering. This panel is taller than the dock gives it, so it scrolls, and a
    // section appended to the end of it landed forty pixels below the fold — present, correct,
    // and invisible unless you already knew to scroll for it. The rest of the tray is set-once
    // material (where the site is, how the air moves); a cover that repaints every square metre of
    // ground and exists for four months of the year is the thing you opened the tray for, on the
    // days it is offered at all. Out of season it demotes itself to a line of explanation at the
    // bottom, which is where reference material belongs.
    const freezing = this.deps.environment.isFreezing();
    this.builtWinter = this.winterSignature();
    if (freezing) this.appendWinter(state.groundCover, true);

    /* ------------------------------------ Site ------------------------------------- */
    this.body.appendChild(el('h3', { class: 'lg-section__title', text: 'Site' }));
    const latitude = this.control(
      'Latitude',
      formatLatitude(state.latitudeDegrees),
      { min: -60, max: 70, step: 1, value: state.latitudeDegrees, format: formatLatitude },
      (value) => {
        this.deps.environment.setLatitude(value);
        this.deps.onChange();
      },
    );
    this.latitude = { input: latitude.input, readout: latitude.readout };
    this.body.appendChild(latitude.root);

    /* ---------------------------------- Weather ------------------------------------ */
    const weather = el('div', { class: 'lg-chips' });
    for (const preset of WEATHER_PRESETS) {
      weather.appendChild(
        el('button', {
          class: `lg-chip${state.weather === preset ? ' is-active' : ''}`,
          text: preset.replace(/-/g, ' '),
          attrs: { type: 'button', 'aria-pressed': state.weather === preset },
          on: {
            click: () => {
              this.deps.environment.setWeather(preset);
              this.deps.onChange();
              this.refresh();
            },
          },
        }),
      );
    }
    this.body.appendChild(el('h3', { class: 'lg-section__title', text: 'Weather preset' }));
    this.body.appendChild(weather);

    if (!freezing) this.appendWinter(state.groundCover, false);
  }

  /** The winter block, wherever it is going: the controls in season, a line of prose out of it. */
  private appendWinter(cover: GroundCoverState, freezing: boolean): void {
    this.body.appendChild(el('h3', { class: 'lg-section__title', text: 'Winter ground' }));
    this.body.appendChild(
      freezing
        ? this.winterSection(cover)
        : el(
            'div',
            { class: 'lg-hint' },
            el('p', {
              text:
                'Snow and ice are laid from 1 November to 15 March. Move the date into winter to ' +
                'switch them on — a cover already set is kept, and comes back when the date does.',
            }),
          ),
    );
  }

  /**
   * Follow what was changed somewhere else — the date crossing the edge of the freezing window, or
   * a cover switched from the chrono bar's copy of these controls.
   *
   * Rebuilding only when one of those actually *changed* is what makes this safe to call on every
   * HUD refresh. The signature deliberately excludes the cover amounts: those move continuously
   * under a slider in this very panel, and rebuilding the DOM under the pointer would drop the drag
   * on its first pixel. Nothing outside this panel can change them anyway.
   */
  syncSeason(): void {
    const signature = this.winterSignature();
    if (signature === this.builtWinter) return;
    this.refresh();
  }

  private winterSignature(): string {
    const { environment } = this.deps;
    const cover = environment.state.groundCover;
    return [
      environment.isFreezing(),
      cover.snow,
      cover.ice,
      cover.snowSwatch,
      cover.iceSwatch,
    ].join('|');
  }

  /**
   * The two covers, each with its variants and its strength.
   *
   * Independent switches, because they are independent facts about a garden: fresh snow with no ice
   * under it, a bare glaze after freezing rain, or snow lying over a frozen crust are three different
   * pictures and all three are ordinary. Ticking one never touches the other.
   */
  private winterSection(cover: GroundCoverState): HTMLElement {
    const section = el('div', { class: 'lg-winter' });

    section.appendChild(
      this.toggle('Snow', cover.snow, (value) => this.setCover({ snow: value })),
    );
    if (cover.snow) {
      section.appendChild(
        this.swatchChips(SNOW_SWATCHES, cover.snowSwatch, (id) => this.setCover({ snowSwatch: id })),
      );
      section.appendChild(el('p', { class: 'lg-legend', text: snowSwatch(cover.snowSwatch).hint }));
      section.appendChild(
        this.control(
          'Depth',
          coverLabel(cover.snowAmount),
          { min: 0.1, max: 1, step: 0.05, value: cover.snowAmount, format: coverLabel },
          // Thinning the fall breaks it into patches rather than fading it, so this reads as a
          // dusting over a garden that is still there rather than as translucent snow.
          (value) => this.setCover({ snowAmount: value }, false),
        ).root,
      );
    }

    section.appendChild(this.toggle('Ice', cover.ice, (value) => this.setCover({ ice: value })));
    if (cover.ice) {
      section.appendChild(
        this.swatchChips(ICE_SWATCHES, cover.iceSwatch, (id) => this.setCover({ iceSwatch: id })),
      );
      section.appendChild(el('p', { class: 'lg-legend', text: iceSwatch(cover.iceSwatch).hint }));
      section.appendChild(
        this.control(
          'Glaze',
          glazeLabel(cover.iceAmount),
          { min: 0.15, max: 1, step: 0.05, value: cover.iceAmount, format: glazeLabel },
          (value) => this.setCover({ iceAmount: value }, false),
        ).root,
      );
    }

    return section;
  }

  /**
   * Apply a cover edit.
   *
   * `rebuild` is false for the sliders and true for everything else: switching a cover on adds its
   * chips and its slider to the panel, but rebuilding the DOM under a slider that is being dragged
   * would drop the pointer on the first pixel of movement.
   */
  private setCover(changes: Partial<GroundCoverState>, rebuild = true): void {
    this.deps.environment.setGroundCover(changes);
    this.deps.onChange();
    if (rebuild) this.refresh();
  }

  /** A row of variant chips, each carrying the colour it will put on the ground. */
  private swatchChips<Id extends string>(
    swatches: readonly WinterSwatch<Id>[],
    activeId: string,
    onPick: (id: Id) => void,
  ): HTMLElement {
    const chips = el('div', { class: 'lg-chips' });
    for (const swatch of swatches) {
      const active = swatch.id === activeId;
      chips.appendChild(
        el(
          'button',
          {
            class: `lg-chip${active ? ' is-active' : ''}`,
            attrs: { type: 'button', 'aria-pressed': active, title: swatch.hint },
            on: { click: () => onPick(swatch.id) },
          },
          el('span', { class: 'lg-chip__dot', style: { background: swatch.color } }),
          el('span', { text: swatch.name }),
        ),
      );
    }
    return chips;
  }

  private toggle(label: string, value: boolean, onChange: (value: boolean) => void): HTMLElement {
    return el(
      'label',
      { class: 'lg-switch' },
      el('span', { class: 'lg-field__label', text: label }),
      el('input', {
        attrs: { type: 'checkbox', checked: value },
        on: { change: (event) => onChange((event.target as HTMLInputElement).checked) },
      }),
      el('span', { class: 'lg-switch__track' }, el('span', { class: 'lg-switch__thumb' })),
    );
  }

  /**
   * A labelled slider with a live readout.
   *
   * Returns its parts as well as its root: a control whose value can be changed from *outside* this
   * panel needs to be updatable in place, and rebuilding the row instead would drop a drag on its
   * first pixel. See {@link syncSite}.
   */
  private control(
    label: string,
    initialText: string,
    range: { min: number; max: number; step: number; value: number; format: (value: number) => string },
    onInput: (value: number) => void,
  ): { root: HTMLElement; input: HTMLInputElement; readout: HTMLElement } {
    const readout = el('span', { class: 'lg-field__value', text: initialText });
    const input = el('input', {
      class: 'lg-slider',
      attrs: {
        type: 'range',
        min: String(range.min),
        max: String(range.max),
        step: String(range.step),
        value: String(range.value),
        'aria-label': label,
      },
      on: {
        input: (event) => {
          const value = Number((event.target as HTMLInputElement).value);
          readout.textContent = range.format(value);
          onInput(value);
        },
      },
    });
    const root = el(
      'div',
      { class: 'lg-control' },
      el('div', { class: 'lg-control__head' }, el('span', { class: 'lg-field__label', text: label }), readout),
      input,
    );
    return { root, input, readout };
  }

  /**
   * Follow a latitude that was changed somewhere else — which today means the site was localized.
   *
   * Moves the existing slider rather than rebuilding the row, for the same reason `syncSeason`
   * rebuilds only when it must: this is called on every HUD refresh, and replacing the element under
   * a pointer that is dragging it would end the drag.
   */
  syncSite(): void {
    if (!this.latitude) return;
    const degrees = this.deps.environment.state.latitudeDegrees;
    const text = formatLatitude(degrees);
    if (this.latitude.readout.textContent === text) return;
    this.latitude.readout.textContent = text;
    this.latitude.input.value = String(Math.round(degrees));
  }

  dispose(): void {
    this.root.remove();
  }
}

/** `40°` — whole degrees, which is all the sun model's precision justifies showing. */
function formatLatitude(degrees: number): string {
  return `${degrees.toFixed(0)}°`;
}

/**
 * How much snow is lying, in words rather than a percentage.
 *
 * "70 %" of a snowfall is not a quantity anyone has ever had an opinion about; "a dusting" and "deep"
 * are the two ends of what the slider actually does to the picture.
 */
function coverLabel(amount: number): string {
  if (amount < 0.35) return 'a dusting';
  if (amount < 0.6) return 'patchy';
  if (amount < 0.85) return 'a cover';
  return 'deep';
}

/** The same, for how hard the ice has taken. */
function glazeLabel(amount: number): string {
  if (amount < 0.4) return 'a film';
  if (amount < 0.7) return 'a glaze';
  return 'a hard freeze';
}
